import type { ReportingTaskStatus } from '@engagement-web-app/common';
import { format } from 'date-fns';
import { RefreshCw } from 'lucide-react';
import { Button } from '@/components/Button';
import { ErrorOverlay } from '@/components/ErrorOverlay';
import { Flex } from '@/components/Flex';
import { Icon } from '@/components/Icon';
import { Skeleton } from '@/components/Skeleton';
import * as shared from '../../tasks.css';
import { useTaskStatusHistory } from '../hooks';
import * as styles from './sections.css';

type StatusHistoryProps = {
    taskId: string;
    onRefresh: () => void;
};

const SKELETON_ROW_COUNT = 3;

/**
 * ETL processing history: a timestamped list of status codes with a manual
 * refresh button.
 */
export function StatusHistory({ taskId, onRefresh }: StatusHistoryProps) {
    const { data, status, refetch } = useTaskStatusHistory(taskId);

    if (status === 'error') return <ErrorOverlay active onRetry={refetch} message="処理履歴の取得に失敗しました。" />;
    if (status === 'pending') return <StatusHistorySkeleton />;

    const handleRefresh = () => {
        refetch();
        onRefresh();
    };

    return <StatusHistoryContent statusHistory={data} onRefresh={handleRefresh} />;
}

type StatusHistoryContentProps = Omit<StatusHistoryProps, 'taskId'> & {
    statusHistory: ReportingTaskStatus[];
};

function StatusHistoryContent({ statusHistory, onRefresh }: StatusHistoryContentProps) {
    return (
        <Flex direction="column" gap="2">
            <Flex align="center" gap="3">
                <span className={shared.sectionLabel}>処理履歴</span>

                {statusHistory ? (
                    <Button type="button" title="最新のステータスを取得" onClick={onRefresh}>
                        <Icon icon={RefreshCw} size="sm" />
                    </Button>
                ) : (
                    <Skeleton.Box width={32} height={32} />
                )}
            </Flex>

            {statusHistory ? (
                <ol className={styles.statusList}>
                    {statusHistory.map((status) => (
                        <li key={status.createdAt}>
                            <span className={styles.statusItem}>{format(status.createdAt, 'yyyy-MM-dd HH:mm:ss')}</span>
                            <span className={styles.statusItem}>{status.code.displayName}</span>
                            {status.code.id === 'ETL_ERROR' && status.errors && status.errors.length > 0 && (
                                <span className={styles.statusError}>{formatErrors(status.errors)}</span>
                            )}
                        </li>
                    ))}
                </ol>
            ) : (
                <Flex direction="column" gap="2">
                    {Array.from({ length: SKELETON_ROW_COUNT }).map((_, i) => (
                        // biome-ignore lint/suspicious/noArrayIndexKey: loading placeholder, rows have no identity
                        <Flex key={i} gap="4">
                            <Skeleton.Line width={140} height="sm" />
                            <Skeleton.Line width={100} height="sm" />
                        </Flex>
                    ))}
                </Flex>
            )}
        </Flex>
    );
}

/**
 * Joins the ETL error entries into a single line-separated string; non-string
 * entries are JSON-serialized.
 */
function formatErrors(errors: readonly unknown[]): string {
    return errors.map((error) => (typeof error === 'string' ? error : JSON.stringify(error))).join('\n');
}

export function StatusHistorySkeleton() {
    return (
        <Flex direction="column" gap="2">
            <Flex align="center" gap="3">
                <span className={shared.sectionLabel}>処理履歴</span>

                <Skeleton.Box width={32} height={32} />
            </Flex>
        </Flex>
    );
}
